import { useState, type FormEvent } from 'react';
import { Button, Icon } from '@etnas/design-system';
import { isApiError } from '@/api';
import { useAuth } from '@/app/auth';
import { API_MODE } from '@/app/config';

/**
 * Two-step sign-in (password → MFA). Shared by workforce login, portal login and the session lock screen.
 * Errors name the problem and keep what was typed (except secrets).
 */
export function SignInForm({
  onSignedIn, presetUsername, lockedUsername, demoUsers, audience = 'workforce',
}: {
  onSignedIn: (r: { sameUser: boolean }) => void;
  presetUsername?: string;
  /** Lock screen: the username is fixed to the person who was signed in */
  lockedUsername?: string;
  demoUsers?: { username: string; label: string }[];
  audience?: 'workforce' | 'portal';
}) {
  const { login, verifyMfa } = useAuth();
  const [step, setStep] = useState<'password' | 'mfa'>('password');
  const [username, setUsername] = useState(lockedUsername ?? presetUsername ?? '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Enter your username and password.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const t = await login(username.trim(), password);
      setMfaToken(t);
      setPassword('');
      setStep('mfa');
    } catch (err) {
      setError(isApiError(err) ? err.detail ?? err.title : 'The sign-in service could not be reached. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await verifyMfa(mfaToken, code.trim());
      onSignedIn(r);
    } catch (err) {
      if (isApiError(err, 'ETNAS_UNAUTHENTICATED')) {
        setStep('password');
        setError('That sign-in step expired. Enter your password again.');
      } else setError(isApiError(err) ? err.detail ?? err.title : 'Could not verify the code.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const errorEl = error ? (
    <div className="ex-field-note ex-fg-critical" role="alert" id="signin-error">
      <Icon name="critical" size={12} knockout="var(--surface)" />
      {error}
    </div>
  ) : null;

  if (step === 'mfa') {
    return (
      <form className="ex-stack" onSubmit={submitCode} aria-labelledby="mfa-title" noValidate>
        <div>
          <h2 id="mfa-title" className="ex-h3">
            Two-step verification
          </h2>
          <p className="ex-small ex-fg-muted" style={{ margin: '4px 0 0' }}>
            Enter the 6-digit code from your authenticator app for <b>{username}</b>.
          </p>
        </div>
        <div className={'ex-field' + (error ? ' is-error' : '')}>
          <label className="ex-field-label" htmlFor="mfa-code">
            Verification code
          </label>
          <div className="ex-field-box">
            <input
              id="mfa-code"
              className="ex-input ex-mono"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              aria-invalid={!!error || undefined}
              aria-describedby={error ? 'signin-error' : 'mfa-hint'}
              autoFocus
            />
          </div>
          {errorEl ?? (
            <div className="ex-field-note ex-fg-muted" id="mfa-hint">
              {API_MODE === 'mock' ? 'Synthetic environment: use 000000.' : 'Codes refresh every 30 seconds.'}
            </div>
          )}
        </div>
        <div className="ex-cluster is-between">
          <Button variant="quiet" type="button" onClick={() => { setStep('password'); setError(null); }}>
            Back
          </Button>
          <Button variant="primary" type="submit" loading={busy} icon="key">
            Verify and sign in
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form className="ex-stack" onSubmit={submitPassword} aria-labelledby="signin-title" noValidate>
      <h2 id="signin-title" className="ex-h3">
        {lockedUsername ? 'Sign in again to continue' : audience === 'portal' ? 'Sign in to your health record' : 'Sign in'}
      </h2>
      <div className="ex-field">
        <label className="ex-field-label" htmlFor="signin-user">
          Username
        </label>
        <div className={'ex-field-box'}>
          <input
            id="signin-user"
            className="ex-input"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            readOnly={!!lockedUsername}
            autoFocus={!lockedUsername}
          />
        </div>
      </div>
      <div className={'ex-field' + (error ? ' is-error' : '')}>
        <label className="ex-field-label" htmlFor="signin-pass">
          Password
        </label>
        <div className="ex-field-box">
          <input
            id="signin-pass"
            className="ex-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={!!error || undefined}
            aria-describedby={error ? 'signin-error' : undefined}
            autoFocus={!!lockedUsername}
          />
        </div>
        {errorEl}
      </div>
      <Button variant="primary" type="submit" loading={busy} icon="arrow-right">
        Continue
      </Button>
      {demoUsers && API_MODE === 'mock' ? (
        <details>
          <summary className="ex-small ex-fg-muted" style={{ cursor: 'pointer' }}>
            Synthetic accounts (password Etnas!2026 · code 000000)
          </summary>
          <div className="ex-auth-users" style={{ marginTop: 8 }}>
            {demoUsers.map((u) => (
              <button
                key={u.username}
                type="button"
                onClick={() => {
                  setUsername(u.username);
                  setPassword('Etnas!2026');
                }}
                title={u.label}
              >
                {u.username}
              </button>
            ))}
          </div>
        </details>
      ) : null}
    </form>
  );
}
